import React, { ReactElement, useMemo, useState } from 'react';
import AddEditAutomaticStatusChangeModal from 'pages/Settings/pages/ConversationSettings/AddEditAutomaticStatusChangeModal';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  ClinicSettingsConversationsChannelStatusFragment as ChannelStatus,
  ClinicSettingsConversationsRuleFragment,
  useGetClinicSettingsConversationsChannelStatusAutoUpdateRulesQuery,
  useDeleteClinicSettingsConversationsChannelStatusAutoUpdateRuleMutation,
  ChannelStatusAction,
  PermissionType,
} from 'shared/types/graphql';
import Dropdown, { IOption, PopoverPlacement } from 'shared/components/Dropdown';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import MoreOptionsButton from 'shared/components/MoreOptionsButton';
import Tooltip from 'shared/components/Tooltip';
import { Button } from '@televet/kibble-ui/build/components/Button';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import {
  Box,
  Flex,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@televet/kibble-ui/build/chakra';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsConversationEvents } from 'shared/providers/TrackingProvider/EventNames';

// The expected option type for all Rules with an action of action UpdateInactiveChannelStatus
export interface IChannelStatusChangeRuleOptions {
  initialStatusIds: string[];
  inactiveStatusId: string;
  inactivityDurationUnit: ChannelStatusInactivityUnits;
  inactivityDurationValue: number;
}

export interface IChannelStatusChangeRule extends Omit<ClinicSettingsConversationsRuleFragment, 'options'> {
  options: IChannelStatusChangeRuleOptions;
}

export interface IStatusDisplay {
  name: string;
  id: string;
  hasWarning: boolean;
  color?: string;
  statusType?: ChannelStatusAction;
}

export interface IStatusRuleDisplay {
  id: string;
  initialStatuses: IStatusDisplay[];
  inactiveStatus: IStatusDisplay;
  inactivityDurationValue: number;
  inactivityDurationUnit: ChannelStatusInactivityUnits;
  hasWarning: boolean;
}

enum StatusRuleInteraction {
  Add = 'Add',
  Edit = 'Edit',
  Delete = 'Delete',
}

const rowOptions: IOption<StatusRuleInteraction>[] = [
  {
    value: StatusRuleInteraction.Edit,
    text: StatusRuleInteraction.Edit,
  },
  {
    value: StatusRuleInteraction.Delete,
    text: StatusRuleInteraction.Delete,
  },
];

export enum ChannelStatusInactivityUnits {
  Minutes = 'Minutes',
  Hours = 'Hours',
  Days = 'Days',
}

export const mapStatusIdToStatusDisplay = (statusId: string, allStatuses: ChannelStatus[]): IStatusDisplay => {
  const existingStatus = allStatuses.find((status) => status.id === statusId);

  return {
    name: existingStatus?.name || 'ERROR',
    id: statusId,
    hasWarning: !existingStatus,
    color: existingStatus?.color || '#484848',
    statusType: existingStatus?.channelStatusAction,
  };
};

interface IAutomaticStatusChangesProps {
  channelStatuses: ChannelStatus[];
}

const AutomaticStatusChanges = ({ channelStatuses }: IAutomaticStatusChangesProps): ReactElement => {
  const { currentClinicId, userHasPermission } = useClinicUser();
  const hasPermission = userHasPermission(PermissionType.WriteClinic);

  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [ruleForEdit, setRuleForEdit] = useState<IStatusRuleDisplay | undefined>();
  const [ruleIdForDelete, setRuleIdForDelete] = useState<string | undefined>();

  const [deleteStatusRule] = useDeleteClinicSettingsConversationsChannelStatusAutoUpdateRuleMutation();
  const toast = useToast();
  const { isOpen: isDeleteConfirmOpen, onOpen: onOpenDeleteConfirm, onClose: onCloseDeleteConfirm } = useDisclosure();
  const cancelDeleteRef = React.useRef(null);
  const {
    data: rulesData,
    loading: isLoading,
    refetch: reloadRules,
  } = useGetClinicSettingsConversationsChannelStatusAutoUpdateRulesQuery({
    variables: {
      clinicId: currentClinicId || '',
    },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
  });
  const { trackEvent } = useTracking();

  const allRules = useMemo<IChannelStatusChangeRule[]>(() => {
    return rulesData?.findManyRules ?? [];
  }, [rulesData]);

  const statusRuleDisplays = useMemo<IStatusRuleDisplay[]>(() => {
    return allRules.map<IStatusRuleDisplay>((rule) => {
      const { initialStatusIds, inactiveStatusId, inactivityDurationValue, inactivityDurationUnit } = rule.options;

      const initStatuses = initialStatusIds.map<IStatusDisplay>((id) =>
        mapStatusIdToStatusDisplay(id, channelStatuses),
      );

      const inactiveStatus = mapStatusIdToStatusDisplay(inactiveStatusId, channelStatuses);
      const hasWarning = initStatuses.some((x) => x.hasWarning) || inactiveStatus.hasWarning;

      const ret: IStatusRuleDisplay = {
        id: rule.id,
        initialStatuses: initStatuses,
        inactiveStatus: inactiveStatus,
        inactivityDurationValue,
        inactivityDurationUnit,
        hasWarning: hasWarning,
      };
      return ret;
    });
  }, [allRules, channelStatuses]);

  const onAddNewRuleBtnClick = (): void => {
    setRuleForEdit(undefined);
    setIsModalOpen(true);
  };

  const onRowOptionSelected = async (
    rule: IStatusRuleDisplay,
    option: IOption<StatusRuleInteraction>,
  ): Promise<void> => {
    switch (option.value) {
      case StatusRuleInteraction.Delete:
        setRuleIdForDelete(rule.id);
        onOpenDeleteConfirm();
        break;
      case StatusRuleInteraction.Edit:
      default:
        setRuleForEdit(rule);
        setIsModalOpen(true);
        break;
    }
  };

  const handleDeleteRule = async (): Promise<void> => {
    onCloseDeleteConfirm();
    if (!ruleIdForDelete) {
      return;
    }
    try {
      await deleteStatusRule({ variables: { ruleId: ruleIdForDelete } });
      trackEvent(SettingsConversationEvents.DeleteAutoUpdateStatusRule);
      toast({ description: 'Changes Saved!' });
      await reloadRules();
    } catch (e) {
      console.error(e);
      toast({ description: 'Something went wrong while deleting the rule. Please try again.', status: 'error' });
    } finally {
      setRuleIdForDelete(undefined);
    }
  };

  const handleModalClose = (): void => {
    setRuleForEdit(undefined);
    setIsModalOpen(false);
  };

  const handleModalSaveSuccess = async (): Promise<void> => {
    trackEvent(SettingsConversationEvents.AddAutoUpdateStatusRule);
    toast({ description: 'Changes Saved!' });
    await reloadRules();
    handleModalClose();
  };

  return (
    <Flex flexDir="column" mb="30px">
      {isModalOpen && (
        <AddEditAutomaticStatusChangeModal
          ruleForEdit={ruleForEdit}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSaveSuccess={handleModalSaveSuccess}
          allRules={allRules}
          allStatuses={channelStatuses}
        />
      )}

      <AlertDialog
        isCentered={true}
        isOpen={isDeleteConfirmOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={onCloseDeleteConfirm}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader color="#000b8e">Delete Status Workflow</AlertDialogHeader>
            <AlertDialogBody>Are you sure? You cannot undo this action.</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelDeleteRef} onClick={onCloseDeleteConfirm}>
                Cancel
              </Button>
              <Button variant="primaryDestructive" onClick={handleDeleteRule} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Flex align="center" justify="space-between" my="15px">
        <Heading size="sm" mt={5}>
          Auto Update Status
        </Heading>
        {hasPermission && (
          <Button iconName="plus" size="xs" variant="secondary" onClick={onAddNewRuleBtnClick}>
            Add Status Workflow
          </Button>
        )}
      </Flex>
      <Text variant="subtle" as="p" size="sm" mb={5}>
        The auto update status workflow allows conversations to automatically change their status after a specified
        period of inactivity.
      </Text>
      <Divider />
      {isLoading && (
        <Box p={3} textAlign="center">
          <LoadingIndicator />
        </Box>
      )}
      {!isLoading && (
        <Box>
          <Flex align="center" justify="space-between" py="10px" px="5px">
            <Flex w="30%">
              <Text size="xs">INITIAL STATUSES</Text>
            </Flex>
            <Flex w="30%">
              <Text size="xs">INACTIVE STATUS</Text>
            </Flex>
            <Flex w="30%">
              <Text size="xs">INACTIVITY DURATION</Text>
            </Flex>
            {hasPermission && (
              <Flex align="center" justify="center" w="10%">
                <Text size="xs">EDIT</Text>
              </Flex>
            )}
          </Flex>
          <Divider />
          <Box>
            {!statusRuleDisplays.length && (
              <Box p={3} textAlign="center">
                <Text fontStyle="italic">There are no automated status workflows created</Text>
              </Box>
            )}
            {statusRuleDisplays.map((rule, index) => {
              const { id, initialStatuses, inactiveStatus, inactivityDurationValue, inactivityDurationUnit } = rule;
              const initialStatusNames = initialStatuses.map((x) => x.name).join(', ');

              return (
                <Flex
                  key={id}
                  align="center"
                  justify="space-between"
                  py="10px"
                  px="5px"
                  bgColor={index && index % 2 ? 'background.default' : 'transparent'}
                >
                  <Flex w="30%">
                    {rule.hasWarning && (
                      <Tooltip
                        content={
                          'This rule references statuses that no longer exist. Please update the rule and save any corrections.'
                        }
                      >
                        <Icon name="warningSign" />
                      </Tooltip>
                    )}
                    <Text isTruncated={true}>{initialStatusNames}</Text>
                  </Flex>
                  <Flex w="30%">
                    <Text size="xs">{inactiveStatus.name}</Text>
                  </Flex>
                  <Flex w="30%">
                    <Text size="xs">{`${inactivityDurationValue} ${inactivityDurationUnit}`}</Text>
                  </Flex>
                  {hasPermission && (
                    <Flex align="center" justify="center" w="10%">
                      <Dropdown
                        options={rowOptions}
                        placement={PopoverPlacement.BottomEnd}
                        onSelect={(_, option): Promise<void> => onRowOptionSelected(rule, option)}
                      >
                        <MoreOptionsButton />
                      </Dropdown>
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </Box>
        </Box>
      )}
      <Divider />
    </Flex>
  );
};

export default AutomaticStatusChanges;
